// 移除自引用的导入语句
interface AsrConfig {
  sampleRate: number;
  channels: number;
  mimeType: string;
}

interface AsrResult {
  text: string;
  isFinal: boolean;
  confidence?: number;
}

// 添加智能对话响应接口
interface SmartDialogResponse {
  type: 'text' | 'complete' | 'error' | 'interrupt';
  text?: string;
  message?: string;
  isComplete?: boolean;
}

class AsrService {
  private config: AsrConfig = {
    sampleRate: 16000, // 阿里云ASR支持16kHz（官方只支持8K和16K）
    channels: 1,
    mimeType: 'audio/wav' // 使用WAV格式以确保兼容性
  };
  
  private websocket: WebSocket | null = null;
  private audioContext: AudioContext | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private processorNode: ScriptProcessorNode | null = null;
  private isRecording: boolean = false;
  private isRecognizing: boolean = false;
  private isAsrReady: boolean = false;
  private pendingAudioData: ArrayBuffer[] = [];
  private isTtsPlaying: boolean = false; // B方案：添加TTS播放状态
  
  // 回调函数
  public onSmartDialogResponse: ((response: SmartDialogResponse) => void) | null = null;
  public onAudioData: ((audioData: ArrayBuffer) => void) | null = null;

  // 修复返回类型错误
  public isRecordingSupported(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    return 'MediaRecorder' in window && 
           navigator.mediaDevices && 
           typeof navigator.mediaDevices.getUserMedia === 'function';
  }
  
  // 获取录音设备
  public async getAudioStream(): Promise<MediaStream> {
    try {
      const constraints: MediaStreamConstraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: this.config.sampleRate,
          channelCount: this.config.channels
        }
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      return stream;
    } catch (error) {
      console.error('获取麦克风权限失败:', error);
      throw new Error('无法访问麦克风，请检查权限设置');
    }
  }
  
  // 全局WebSocket协议验证函数
  private validateWebSocketProtocol(): void {
    if (typeof window === 'undefined') return;
    
    const isHttps = window.location.protocol === 'https:';
    const hostname = window.location.hostname;
    
    // 在HTTPS环境下，强制验证WebSocket协议
    if (isHttps && hostname.includes('younggo.click')) {
      console.log('✅ WebSocket协议验证通过: HTTPS环境下的younggo.click域名');
    }
  }
  
  // 标记ASR就绪并发送缓存的音频数据
  private markAsrReady(): void {
    if (this.isAsrReady) {
      return;
    }
    
    this.isAsrReady = true;
    console.log('✅ ASR就绪，开始发送缓存的音频数据，共', this.pendingAudioData.length, '个数据包');
    
    // 发送所有缓存的音频数据
    const cachedData = [...this.pendingAudioData];
    this.pendingAudioData = [];
    
    cachedData.forEach((data, index) => {
      setTimeout(() => {
        this.sendAudioDataToWebsocket(data);
      }, index * 10); // 添加微小延迟避免同时发送
    });
  }
  
  // 辅助方法：发送音频数据到WebSocket
  private sendAudioDataToWebsocket(audioData: ArrayBuffer): void {
    if (this.isTtsPlaying) {
      // TTS播放期间不发送音频数据，防止自反馈循环
      return;
    }
    
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      this.websocket.send(audioData);
    } else {
      // WebSocket未就绪，缓存音频数据
      this.pendingAudioData.push(audioData);
    }
  }
  
  // 开始录音（使用Web Audio API处理音频）
  public async startRecording(
    onResult: (result: AsrResult) => void,
    onError?: (error: string) => void,
    sessionId?: number,
    childAge?: number,
    onSmartDialogResponse?: (response: SmartDialogResponse) => void
  ): Promise<void> {
    // 执行协议验证
    this.validateWebSocketProtocol();
    
    if (!this.isRecordingSupported()) {
      throw new Error('当前浏览器不支持语音识别功能');
    }
    
    if (this.isRecording) {
      throw new Error('已经在录音中');
    }
    
    // 设置智能对话回调
    this.onSmartDialogResponse = onSmartDialogResponse || null;
    
    try {
      console.log('🎙️ 开始获取麦克风权限...');
      // 获取音频流
      const stream = await this.getAudioStream();
      console.log('✅ 麦克风权限获取成功，音频流已创建');
      
      // 创建AudioContext处理音频
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      this.audioContext = new AudioContext({ sampleRate: this.config.sampleRate });
      console.log('🔊 AudioContext已创建，状态:', this.audioContext.state);
      
      // 创建音频源节点
      this.sourceNode = this.audioContext.createMediaStreamSource(stream);
      console.log('📡 音频源节点已创建');
      
      // 创建脚本处理器节点
      const bufferSize = 1024;
      this.processorNode = this.audioContext.createScriptProcessor(bufferSize, 1, 1);
      console.log('⚙️ 脚本处理器节点已创建，bufferSize:', bufferSize);
      
      let audioProcessCount = 0;
      // 设置音频处理回调
      this.processorNode.onaudioprocess = (event: AudioProcessingEvent) => {
        audioProcessCount++;
        if (audioProcessCount <= 5) {
          console.log(`🎵 onaudioprocess 被调用 #${audioProcessCount}, 输入缓冲区长度:`, event.inputBuffer.length);
        }
        
        if (!this.websocket) {
          if (audioProcessCount <= 5) {
            console.log('❌ WebSocket未初始化，跳过音频处理');
          }
          return;
        }
        
        const inputBuffer = event.inputBuffer;
        const inputData = inputBuffer.getChannelData(0);
        
        // 转换为16位PCM
        const pcmData = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          pcmData[i] = Math.max(-1, Math.min(1, inputData[i])) * 32767;
        }
        
        this.sendAudioDataToWebsocket(pcmData.buffer);
      };
      
      // 连接音频节点
      this.sourceNode.connect(this.processorNode);
      console.log('🔌 音频节点已连接');
      
      // 关键修复：先连接WebSocket，再激活音频处理
      console.log('🌐 开始连接WebSocket...');
      await this.connectWebSocket(onResult, onError, sessionId, childAge);
      console.log('✅ WebSocket连接已建立');
      
      // 现在将processorNode连接到audioContext.destination，激活音频处理
      this.processorNode.connect(this.audioContext.destination);
      console.log('🔊 音频处理器已连接到输出，音频处理已激活');
      
      // 确保AudioContext处于运行状态
      if (this.audioContext.state === 'suspended') {
        console.log('⏸️ AudioContext处于挂起状态，等待用户手势...');
        try {
          await this.audioContext.resume();
          console.log('▶️ AudioContext已恢复运行');
        } catch (error) {
          console.warn('⚠️ AudioContext恢复失败:', error);
        }
      } else {
        console.log('▶️ AudioContext已在运行状态');
      }
      
      // 在WebSocket连接建立后立即开始录音状态
      this.isRecording = true;
      this.isRecognizing = true;
      console.log('🎤 录音已开始，准备接收音频数据');
      
    } catch (error: any) {
      console.error('开始录音失败:', error);
      console.error('错误详情:', error.stack);
      if (onError) {
        let errorMessage = error.message || '录音启动失败';
        if (error.name === 'NotAllowedError') {
          errorMessage = '请允许麦克风访问权限';
        } else if (error.name === 'NotFoundError') {
          errorMessage = '未找到麦克风设备';
        } else if (error.name === 'NotReadableError') {
          errorMessage = '麦克风被其他应用占用或无法访问';
        }
        onError(errorMessage);
      }
      this.cleanup();
      throw error;
    }
  }
  
  // 停止录音
  public stopRecording(): void {
    this.isRecording = false;
    this.isRecognizing = false;
    this.cleanup();
  }
  
  // 清理资源
  private cleanup(): void {
    if (this.processorNode) {
      this.processorNode.disconnect();
      this.processorNode = null;
    }
    
    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }
    
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    
    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }
    
    // 清理回调和状态
    this.onSmartDialogResponse = null;
    this.onAudioData = null;
    this.isAsrReady = false;
    this.pendingAudioData = [];
  }
  
  // 连接ASR WebSocket
  private async connectWebSocket(
    onResult: (result: AsrResult) => void,
    onError?: (error: string) => void,
    sessionId?: number,
    childAge?: number
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // 获取当前页面协议
        const currentProtocol = window.location.protocol;
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        
        // 构建WebSocket URL
        let wsProtocol = 'ws://';
        if (currentProtocol === 'https:' || isLocalhost) {
          wsProtocol = 'wss://';
        }
        
        // 构建完整的WebSocket URL
        const baseUrl = `${wsProtocol}${window.location.host}`;
        let fullWsUrl = `${baseUrl}/api/asr`;
        
        // 添加查询参数
        const params = new URLSearchParams();
        if (sessionId !== undefined) {
          params.append('sessionId', sessionId.toString());
        }
        if (childAge !== undefined) {
          params.append('childAge', childAge.toString());
        }
        
        if (params.toString()) {
          fullWsUrl += `?${params.toString()}`;
        }
        
        console.log('🔗 WebSocket连接URL:', fullWsUrl);
        
        this.websocket = new WebSocket(fullWsUrl);
        
        this.websocket.onopen = () => {
          console.log('ASR WebSocket连接成功');
          resolve();
        };
        
        this.websocket.onmessage = (event) => {
          if (typeof event.data === 'string') {
            try {
              const message = JSON.parse(event.data);
              
              console.log('📨 收到WebSocket消息:', message);
              
              // 处理ASR就绪通知
              if (message.type === 'asr_ready') {
                console.log('✅ 收到ASR就绪通知');
                this.markAsrReady();
                return;
              }
              
              // 处理打断通知
              if (message.type === 'interrupt') {
                console.log('🛑 收到打断通知，立即停止语音播放');
                // 通知前端立即停止播放
                if (this.onSmartDialogResponse) {
                  this.onSmartDialogResponse({type: 'interrupt'});
                }
                return;
              }
              
              // 处理ASR识别结果
              if (message.type === 'result') {
                if (onResult) {
                  onResult({
                    text: message.text || '',
                    isFinal: false
                  });
                }
              }
              // 处理错误消息
              else if (message.type === 'error') {
                if (onError) {
                  onError(message.message || '未知错误');
                }
              }
              // 处理智能对话响应
              else if (message.type === 'text' || message.type === 'complete') {
                if (this.onSmartDialogResponse) {
                  this.onSmartDialogResponse(message);
                }
              }
              // 处理TTS音频数据（base64格式）
              else if (message.type === 'voice_data' && message.data) {
                console.log('🔊 收到TTS音频数据（base64格式），长度:', message.data.length);
                if (this.onAudioData) {
                  try {
                    // 将base64字符串转换为ArrayBuffer
                    const binary = atob(message.data);
                    const bytes = new Uint8Array(binary.length);
                    for (let i = 0; i < binary.length; i++) {
                      bytes[i] = binary.charCodeAt(i);
                    }
                    this.onAudioData(bytes.buffer);
                  } catch (error) {
                    console.error('🔊 转换base64音频数据失败:', error);
                  }
                }
              }
            } catch (e) {
              console.warn('⚠️ 解析WebSocket消息失败:', e);
            }
          }
          // 处理二进制消息（TTS音频数据）
          else if (event.data instanceof ArrayBuffer) {
            console.log('🔊 收到TTS音频数据，长度:', event.data.byteLength);
            if (this.onAudioData) {
              this.onAudioData(event.data);
            }
          }
        };
        
        this.websocket.onerror = (error) => {
          console.error('ASR WebSocket连接错误:', error);
          let errorMessage = '语音识别服务连接失败';
          if (this.websocket && this.websocket.readyState === WebSocket.CLOSED) {
            errorMessage = '语音识别服务不可用，请检查后端是否正常运行';
          }
          if (onError) {
            onError(errorMessage);
          }
          reject(new Error(errorMessage));
        };
        
        this.websocket.onclose = (event) => {
          console.log('ASR WebSocket连接关闭:', event.code, event.reason);
          if (event.code !== 1000) {
            let closeMessage = `WebSocket连接异常关闭 (code: ${event.code})`;
            if (event.reason) {
              closeMessage += `: ${event.reason}`;
            }
            console.error(closeMessage);
            if (onError && !this.isRecognizing) {
              onError('语音识别服务连接中断，请检查网络或联系管理员');
            }
          }
        };
        
      } catch (error) {
        console.error('创建ASR WebSocket失败:', error);
        if (onError) {
          onError('无法创建语音识别连接，请检查浏览器支持情况');
        }
        reject(error);
      }
    });
  }
  
  // B方案：TTS开始时调用
  public onTtsStart(): void {
    this.isTtsPlaying = true;
    console.log('🔊 TTS开始播放，暂停ASR音频发送');
    
    // 通知后端设置会话状态为SPEAKING
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      this.websocket.send(JSON.stringify({
        type: 'tts_start'
      }));
    }
  }
  
  // B方案：TTS结束时调用
  public onTtsEnd(): void {
    this.isTtsPlaying = false;
    console.log('🔇 TTS结束播放，恢复ASR音频发送');
    
    // 通知后端设置会话状态为LISTENING
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      this.websocket.send(JSON.stringify({
        type: 'tts_end'
      }));
    }
  }
  
  // 检查是否正在录音
  public isRecordingActive(): boolean {
    return this.isRecording;
  }
  
  // 检查是否正在识别
  public isRecognizingActive(): boolean {
    return this.isRecognizing;
  }
  
  // 更新配置
  public updateConfig(newConfig: Partial<AsrConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }
}

// 创建全局ASR服务实例
const asrService = new AsrService();

export default asrService;
export type { AsrConfig, AsrResult, SmartDialogResponse };